import React, { useState, useEffect, useRef } from 'react';

function Sidebar({ 
  isOpen, 
  toggleSidebar, 
  allTags, 
  selectedTag, 
  filterByTag, 
  toggleArchivedView, 
  showArchived,
  sortCards,
  currentSortMethod = 'newest',
  hiddenTags = [],
  toggleTagVisibility,
  hideCardsWithHiddenTags,
  toggleHideCardsWithHiddenTags,
  cards
}) {
  const sidebarRef = useRef(null);
  // Set all expandedSections to false by default
  const [expandedSections, setExpandedSections] = useState({
    tags: false,
    categories: false,
    sort: false,
    display: false
  });
  const [searchTag, setSearchTag] = useState('');

  // Calculate tag counts
  const tagCounts = React.useMemo(() => {
    const counts = {};
    if (Array.isArray(cards)) {
      cards.forEach(card => {
        if (Array.isArray(card.tags)) {
          card.tags.forEach(tag => {
            counts[tag] = (counts[tag] || 0) + 1;
          });
        }
      });
    }
    return counts;
  }, [cards]);

  // Separate tags into regular tags and category tags (>15 cards)
  const { regularTags, categoryTags } = React.useMemo(() => {
    const regular = [];
    const categories = [];
    
    allTags.forEach(tag => {
      if (tagCounts[tag] >= 15) {
        categories.push(tag);
      } else {
        regular.push(tag);
      }
    });
    
    return { regularTags: regular, categoryTags: categories };
  }, [allTags, tagCounts]);

  // Handle outside clicks to close sidebar
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && 
          !event.target.classList.contains('sidebar-trigger')) {
        toggleSidebar(); // Close sidebar when clicking outside
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleSidebar]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  // Filter tags based on search
  const filteredRegularTags = regularTags.filter(tag => 
    tag.toLowerCase().includes(searchTag.toLowerCase())
  );
  
  const filteredCategoryTags = categoryTags.filter(tag => 
    tag.toLowerCase().includes(searchTag.toLowerCase())
  );

  return (
    <>
      <div className="sidebar-trigger" onClick={toggleSidebar}></div>
      
      <div className={`sidebar ${isOpen ? 'open' : ''}`} ref={sidebarRef}>
        <div className="sidebar-header">
          <h2>Card Management</h2>
          <button className="close-sidebar" onClick={toggleSidebar}>×</button>
        </div>

        {/* Display Section */}
        <div className="sidebar-section">
          <div 
            className="section-header" 
            onClick={() => toggleSection('display')}
          >
            <h3>Display Options</h3>
            <span className="toggle-icon">
              {expandedSections.display ? '▼' : '►'}
            </span>
          </div>
          
          {expandedSections.display && (
            <div className="section-content">
              <button 
                className={`display-option ${showArchived ? 'active' : ''}`}
                onClick={toggleArchivedView}
              >
                {showArchived ? "Show Active Cards" : "Show Archived Cards"}
              </button>
              {/* Hide cards with hidden tags checkbox */}
              <div className="custom-checkbox">
                <input
                  type="checkbox"
                  id="hideTaggedCards"
                  checked={hideCardsWithHiddenTags}
                  onChange={() => toggleHideCardsWithHiddenTags()}
                />
                <label htmlFor="hideTaggedCards">Toggle visibility of cards?</label>
              </div>
            </div>
          )}

        </div>
        
        {/* Categories Section */}
        <div className="sidebar-section">
          <div 
            className="section-header" 
            onClick={() => toggleSection('categories')}
          >
            <h3>Categories</h3>
            <span className="toggle-icon">
              {expandedSections.categories ? '▼' : '►'}
            </span>
          </div>
          
          {expandedSections.categories && (
            <div className="section-content">
              {filteredCategoryTags.length > 0 ? (
                filteredCategoryTags.map((tag, index) => (
                  <div 
                    key={index} 
                    className={`sidebar-tag-row category-tag-row ${hiddenTags.includes(tag) ? 'hidden-tag' : ''}`}
                  >
                    <div 
                      className={`sidebar-tag ${selectedTag === tag ? 'active' : ''} ${hiddenTags.includes(tag) ? 'faded' : ''}`}
                      onClick={() => filterByTag(tag)}
                    >
                      <span className="tag-name">{tag}</span>
                      <span className="tag-count">{tagCounts[tag]}</span>
                    </div>
                    <button 
                      className="visibility-toggle-text" 
                      onClick={() => toggleTagVisibility(tag)}
                    >
                      {hiddenTags.includes(tag) ? "Show" : "Hide"}
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty-category-message">
                  No categories found
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Regular Tags Section */}
        <div className="sidebar-section">
          <div 
            className="section-header" 
            onClick={() => toggleSection('tags')}
          >
            <h3>Tags</h3>
            <span className="toggle-icon">
              {expandedSections.tags ? '▼' : '►'}
            </span>
          </div>
          
          {expandedSections.tags && (
            <div className="section-content">
              {/* Search at the top */}
              <div className="tag-search">
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchTag}
                  onChange={(e) => setSearchTag(e.target.value)}
                />
              </div>
              
              
              {/* All Cards option */}
              <div 
                className={`sidebar-tag all-cards ${selectedTag === null ? 'active' : ''}`}
                onClick={() => filterByTag(null)}
              >
                All Cards
              </div>
              
              {/* Regular tags list with visibility toggles */}
              {filteredRegularTags.map((tag, index) => (
                <div 
                  key={index} 
                  className={`sidebar-tag-row ${hiddenTags.includes(tag) ? 'hidden-tag' : ''}`}
                >
                  <div 
                    className={`sidebar-tag ${selectedTag === tag ? 'active' : ''} ${hiddenTags.includes(tag) ? 'faded' : ''}`}
                    onClick={() => filterByTag(tag)}
                  >
                    <span className="tag-name">{tag}</span>
                    <span className="tag-count">{tagCounts[tag]}</span>
                  </div>
                  <button 
                    className="visibility-toggle-text" 
                    onClick={() => toggleTagVisibility(tag)}
                  >
                    {hiddenTags.includes(tag) ? "Show" : "Hide"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Sort Section */}
        <div className="sidebar-section">
          <div 
            className="section-header" 
            onClick={() => toggleSection('sort')}
          >
            <h3>Sort Options</h3>
            <span className="toggle-icon">
              {expandedSections.sort ? '▼' : '►'}
            </span>
          </div>
          
          {expandedSections.sort && (
            <div className="section-content">
              <div 
                className={`sidebar-option ${currentSortMethod === 'newest' ? 'active' : ''}`}
                onClick={() => sortCards('newest')}
              >
                Newest First
              </div>
              <div 
                className={`sidebar-option ${currentSortMethod === 'oldest' ? 'active' : ''}`}
                onClick={() => sortCards('oldest')}
              >
                Oldest First
              </div>
              <div 
                className={`sidebar-option ${currentSortMethod === 'alphabetical' ? 'active' : ''}`}
                onClick={() => sortCards('alphabetical')}
              >
                Alphabetical (A-Z)
              </div>
              <div 
                className={`sidebar-option ${currentSortMethod === 'reverseAlphabetical' ? 'active' : ''}`}
                onClick={() => sortCards('reverseAlphabetical')}
              >
                Alphabetical (Z-A)
              </div>
            </div>
          )}
        </div>
        
        
        
        {/* Footer with version or extra info */}
        <div className="sidebar-footer">
          <small>Card Manager v1.1</small>
          <br></br>
          <small>Created by <a href="https://github.com/DevRanbir">Ranbir Khurana</a></small>
        </div>
      </div>
    </>
  );
}

export default Sidebar;